/**
 * Liabilities Form Component
 */

import React, { useState } from 'react';
import { FormData, Debt } from '../types';

interface LiabilitiesFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const LiabilitiesForm: React.FC<LiabilitiesFormProps> = ({ formData, onChange, onNext, onBack }) => {
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);
  const [showLoanForm, setShowLoanForm] = useState(false);
  const [showOtherDebtForm, setShowOtherDebtForm] = useState(false);

  const liabilities = formData.liabilities || {};

  const addCreditCard = (debt: Debt) => {
    onChange('liabilities', {
      ...liabilities,
      creditCards: [...(liabilities.creditCards || []), debt],
    });
    setShowCreditCardForm(false);
  };

  const addPersonalLoan = (debt: Debt) => {
    onChange('liabilities', {
      ...liabilities,
      personalLoans: [...(liabilities.personalLoans || []), debt],
    });
    setShowLoanForm(false);
  };

  const addOtherDebt = (debt: Debt) => {
    onChange('liabilities', {
      ...liabilities,
      other: [...(liabilities.other || []), debt],
    });
    setShowOtherDebtForm(false);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Liabilities Information
      </h2>

      <p className="text-gray-600 mb-6">
        Please provide information about your debts and liabilities.
      </p>

      {/* Credit Cards */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Credit Card Debt</h3>
          {!showCreditCardForm && (
            <button
              onClick={() => setShowCreditCardForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Credit Card
            </button>
          )}
        </div>

        {liabilities.creditCards && liabilities.creditCards.length > 0 && (
          <div className="space-y-2 mb-4">
            {liabilities.creditCards.map((card, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">Credit Card #{index + 1}</div>
                <div className="text-sm text-gray-600">
                  Balance: ${card.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreditCardForm && (
          <div className="bg-red-50 p-4 rounded-lg space-y-4">
            <input
              type="number"
              placeholder="Credit Card Balance ($)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addCreditCard({ amount: Number((e.target as HTMLInputElement).value) });
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="flex gap-2">
              <button
                onClick={() => addCreditCard({ amount: Number((document.querySelector('input') as HTMLInputElement)?.value || 0) })}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
              <button
                onClick={() => setShowCreditCardForm(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Personal Loans */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Personal Loans</h3>
          {!showLoanForm && (
            <button
              onClick={() => setShowLoanForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Loan
            </button>
          )}
        </div>

        {liabilities.personalLoans && liabilities.personalLoans.length > 0 && (
          <div className="space-y-2 mb-4">
            {liabilities.personalLoans.map((loan, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">Personal Loan #{index + 1}</div>
                <div className="text-sm text-gray-600">
                  Amount: ${loan.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showLoanForm && (
          <div className="bg-red-50 p-4 rounded-lg space-y-4">
            <input
              type="number"
              placeholder="Loan Amount ($)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addPersonalLoan({ amount: Number((e.target as HTMLInputElement).value) });
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="flex gap-2">
              <button
                onClick={() => addPersonalLoan({ amount: Number((document.querySelector('input') as HTMLInputElement)?.value || 0) })}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
              <button
                onClick={() => setShowLoanForm(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Other Debt */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-gray-900">Other Debt</h3>
          {!showOtherDebtForm && (
            <button
              onClick={() => setShowOtherDebtForm(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              + Add Other Debt
            </button>
          )}
        </div>

        {liabilities.other && liabilities.other.length > 0 && (
          <div className="space-y-2 mb-4">
            {liabilities.other.map((debt, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="font-medium">{debt.description || 'Other Debt'}</div>
                <div className="text-sm text-gray-600">
                  Amount: ${debt.amount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {showOtherDebtForm && (
          <div className="bg-red-50 p-4 rounded-lg space-y-4">
            <textarea
              placeholder="Description of debt"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="number"
              placeholder="Amount ($)"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const desc = (document.querySelector('textarea') as HTMLTextAreaElement)?.value;
                  const amount = Number((e.target as HTMLInputElement).value);
                  addOtherDebt({ amount, description: desc });
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const desc = (document.querySelector('textarea') as HTMLTextAreaElement)?.value;
                  const amount = Number((document.querySelectorAll('input')[1] as HTMLInputElement)?.value || 0);
                  addOtherDebt({ amount, description: desc });
                }}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
              <button
                onClick={() => setShowOtherDebtForm(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300"
        >
          Next: Income
        </button>
      </div>
    </div>
  );
};

export default LiabilitiesForm;